import { getApi, delApi, get, post } from "@/shared/utils/fetchApi";

export interface AgentIntegration {
  id: string;
  agent_base_id: string;
  integration_base_id: string;
  agent_name: string;
  integration_name: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  integration_base?: {
    id: string;
    name: string;
    label: string;
    logo_url: string | null;
    description: string | null;
    classification: string | null;
    status?: string;
    // description traduzível usada por getIntegrationDescription (paridade catálogo)
    settings?: { description?: string | { en: string; pt: string } } | null;
  };
}

export interface AgentIntegrationResponse {
  success: boolean;
  data: AgentIntegration[];
  total: number;
  environment: string;
}

export interface CreateAgentIntegrationData {
  agent_base_id: string;
  integration_base_id: string;
  created_by?: string;
}

export class AgentIntegrationsService {
  private static baseUrl = "/agent-base/integrations";

  /**
   * Buscar integrações de um agente específico
   */
  static async getByAgentId(
    agentId: string,
    includeDetails: boolean = true,
  ): Promise<AgentIntegrationResponse> {
    const qs = new URLSearchParams({ agent_base_id: agentId });
    if (includeDetails) qs.append("include_details", "true");
    const res = await get<{
      data: AgentIntegration[];
      pagination?: { total_items: number };
    }>(`/agent-integrations?${qs.toString()}`);
    return {
      success: true,
      data: res.data,
      total: res.pagination?.total_items ?? res.data.length,
      environment: "",
    };
  }

  /**
   * Buscar TODOS os vínculos agente↔integração de uma organização em uma única
   * chamada (substitui o N+1 de `getByAgentId` por agente na tela /agents).
   * Pagina internamente (limit 200) para cobrir orgs com muitos vínculos sem
   * truncar silenciosamente. O caller agrupa por `agent_base_id`.
   */
  static async getAllByOrg(
    organizationId: string,
  ): Promise<AgentIntegration[]> {
    const limit = 200;

    const all: AgentIntegration[] = [];
    let page = 1;
    // Loop de páginas: para quando cobrir total_pages (ou vier página vazia).
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const qs = new URLSearchParams({
        organization_id: organizationId,
        page: String(page),
        limit: String(limit),
        // include_details embute integration_base (logo/label/desc) em cada
        // vínculo → dispensa o catálogo separado de integration-bases na tela.
        include_details: "true",
      });
      const res = await get<{
        data: AgentIntegration[];
        pagination?: { total_pages?: number };
      }>(`/agent-integrations?${qs.toString()}`);
      all.push(...res.data);
      // Para por total_pages quando a API informa; senão cai no fallback de
      // página incompleta (< limit) — evita truncar se a metadata vier ausente.
      const totalPages = res.pagination?.total_pages;
      if (
        totalPages !== undefined ? page >= totalPages : res.data.length < limit
      )
        break;
      page += 1;
    }
    return all;
  }

  /**
   * Buscar agentes que usam uma integração específica
   */
  static async getByIntegrationId(
    integrationId: string,
  ): Promise<AgentIntegrationResponse> {
    const response = await getApi<AgentIntegrationResponse>(
      `${this.baseUrl}/integration/${integrationId}`,
    );
    return response;
  }

  /**
   * Buscar com filtros e paginação
   */
  static async getAll(
    filters: {
      agent_base_id?: string;
      integration_base_id?: string;
      agent_name?: string;
      integration_name?: string;
      organization_id?: string;
      page?: number;
      limit?: number;
      sort_by?: "created_at" | "updated_at" | "agent_name" | "integration_name";
      sort_order?: "asc" | "desc";
      // snake_case como as demais: os filtros vão para a query com o nome que
      // têm aqui, e `includeDetails` chegava na api como chave desconhecida.
      include_details?: boolean;
    } = {},
  ): Promise<AgentIntegrationResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await getApi<AgentIntegrationResponse>(
      `${this.baseUrl}?${params.toString()}`,
    );
    return response;
  }

  /**
   * Criar nova integração para agente
   */
  static async create(
    data: CreateAgentIntegrationData,
  ): Promise<AgentIntegrationResponse> {
    const { created_by: _drop, ...body } = data;
    void _drop;
    const res = await post<AgentIntegration>("/agent-integrations", body);
    return { success: true, data: [res], total: 1, environment: "" };
  }

  /**
   * Remover integração específica do agente
   */
  static async remove(
    agentId: string,
    integrationId: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await delApi<{ success: boolean; message: string }>(
      `${this.baseUrl}/${agentId}/${integrationId}`,
    );
    return response;
  }

  /**
   * Remover integração por ID
   */
  static async removeById(
    id: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await delApi<{ success: boolean; message: string }>(
      `${this.baseUrl}/${id}`,
    );
    return response;
  }

  /**
   * Obter estatísticas de uso
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async getStats(organizationId?: string): Promise<any> {
    const params = organizationId ? `?organization_id=${organizationId}` : "";
    const response = await getApi(`${this.baseUrl}/stats${params}`);
    return response;
  }
}
